/**
 * Frontend API client + real-time connection manager.
 * Singleton pattern matching the existing codebase (appClock, renderer, etc.).
 *
 * Supports two connection modes:
 *   - SSE (legacy, for Express dev server)
 *   - WebSocket (new, via Durable Object for Cloudflare)
 *
 * Automatically adds auth headers to all REST calls.
 */

import { authManager } from '@/auth/auth-manager';
import type { TaskRecord, DeliverableRecord, DeliverableContent, TaskPlan } from '@/data/types';

export interface TaskDeliverable {
  title: string;
  content: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  workerId: string;
  timestamp: number;
  deliverable?: TaskDeliverable;
  taskPlan?: TaskPlan;
}

export interface SSEEvent {
  type: string;
  data: Record<string, unknown>;
  timestamp: number;
}

type SSEListener = (event: SSEEvent) => void;
type ConnectionListener = (connected: boolean) => void;

class APIClient {
  private baseUrl = '/api';
  private eventSource: EventSource | null = null;
  private webSocket: WebSocket | null = null;
  private sseListeners: Map<string, Set<SSEListener>> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectionListeners: Set<ConnectionListener> = new Set();
  private _connected = false;
  private _useWebSocket = false;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private wsReconnectAttempt = 0;
  private readonly WS_MAX_RECONNECT_ATTEMPTS = 10;
  private readonly WS_BACKOFF_BASE_MS = 1000; // 1s → 2s → 4s → ... → 30s cap
  private readonly WS_BACKOFF_CAP_MS = 30_000;
  private visibilityHandler: (() => void) | null = null;

  /**
   * Enable WebSocket mode (for Cloudflare Durable Object).
   * When disabled (default), uses SSE for backward compatibility.
   */
  setWebSocketMode(enabled: boolean): void {
    this._useWebSocket = enabled;
  }

  // ── REST Methods ──────────────────────────────────────────

  async sendChat(message: string, workerId: string): Promise<{
    message: string;
    workerAction?: string;
    deliverable?: TaskDeliverable;
    taskPlan?: TaskPlan;
    tokensUsed?: number;
  }> {
    return this.fetchWithRetry(async () => {
      const res = await fetch(`${this.baseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authManager.getAuthHeaders(),
        },
        body: JSON.stringify({ message, workerId }),
      });
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`Chat failed: ${res.status} ${err}`);
      }
      return res.json();
    });
  }

  async getAgentStatus(): Promise<{ state: string; connectedClients: number }> {
    const res = await fetch(`${this.baseUrl}/agent/status`, {
      headers: authManager.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Status failed: ${res.status}`);
    return res.json();
  }

  /**
   * Pre-wake the agent. Retries up to 3 times with backoff.
   * Useful on interior entry to warm up a cold Moltworker.
   */
  async wakeAgent(): Promise<void> {
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await fetch(`${this.baseUrl}/agent/wake`, {
          method: 'POST',
          headers: authManager.getAuthHeaders(),
        });
        if (res.ok) return;
      } catch (err) {
        console.warn(`[APIClient] Wake attempt ${attempt + 1} failed:`, err);
      }
      if (attempt < 2) {
        await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
      }
    }
    console.warn('Agent wake failed after 3 attempts — will retry on first chat');
  }

  // ── Generic REST helpers ────────────────────────────────────

  async post(path: string, body: unknown): Promise<Response> {
    return fetch(`${this.baseUrl}${path.startsWith('/api') ? path.slice(4) : path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authManager.getAuthHeaders(),
      },
      body: JSON.stringify(body),
    });
  }

  async get(path: string): Promise<Response> {
    return fetch(`${this.baseUrl}${path.startsWith('/api') ? path.slice(4) : path}`, {
      headers: authManager.getAuthHeaders(),
    });
  }

  // ── Task + Deliverable Methods ──────────────────────────────

  async getTasks(filters?: { status?: string; workerId?: string }): Promise<TaskRecord[]> {
    const params = new URLSearchParams();
    if (filters?.status) params.set('status', filters.status);
    if (filters?.workerId) params.set('workerId', filters.workerId);
    const query = params.toString() ? `?${params.toString()}` : '';

    const res = await fetch(`${this.baseUrl}/tasks${query}`, {
      headers: authManager.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Tasks fetch failed: ${res.status}`);
    const data = await res.json() as { tasks: TaskRecord[] };
    return data.tasks;
  }

  async executePlan(plan: TaskPlan): Promise<{ planId: string; tasks: TaskRecord[] }> {
    const res = await fetch(`${this.baseUrl}/tasks/execute`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...authManager.getAuthHeaders(),
      },
      body: JSON.stringify(plan),
    });
    if (!res.ok) {
      const err = await res.text();
      throw new Error(`Plan execution failed: ${res.status} ${err}`);
    }
    return res.json();
  }

  async getDeliverables(workerId?: string): Promise<DeliverableRecord[]> {
    const query = workerId ? `?workerId=${encodeURIComponent(workerId)}` : '';
    const res = await fetch(`${this.baseUrl}/deliverables${query}`, {
      headers: authManager.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Deliverables fetch failed: ${res.status}`);
    const data = await res.json() as { deliverables: DeliverableRecord[] };
    return data.deliverables;
  }

  async getDeliverableContent(id: string): Promise<DeliverableContent> {
    const res = await fetch(`${this.baseUrl}/deliverables/${encodeURIComponent(id)}`, {
      headers: authManager.getAuthHeaders(),
    });
    if (!res.ok) throw new Error(`Deliverable fetch failed: ${res.status}`);
    return res.json();
  }

  // ── Retry Logic ─────────────────────────────────────────────

  private async fetchWithRetry<T>(
    fn: () => Promise<T>,
    maxRetries = 2,
  ): Promise<T> {
    let lastError: Error | null = null;
    for (let i = 0; i <= maxRetries; i++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err instanceof Error ? err : new Error(String(err));
        if (i < maxRetries) {
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        }
      }
    }
    throw lastError;
  }

  // ── Connection Health ───────────────────────────────────────

  get connected(): boolean {
    return this._connected;
  }

  onConnectionChange(listener: ConnectionListener): () => void {
    this.connectionListeners.add(listener);
    listener(this._connected);
    return () => this.connectionListeners.delete(listener);
  }

  private setConnected(connected: boolean): void {
    if (this._connected !== connected) {
      this._connected = connected;
      this.connectionListeners.forEach(fn => fn(connected));
    }
  }

  // ── Real-time Connection ──────────────────────────────────

  connectSSE(): void {
    if (this._useWebSocket) {
      this.connectWebSocket();
    } else {
      this.connectEventSource();
    }
  }

  disconnectSSE(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }

    this.eventSource?.close();
    this.eventSource = null;

    if (this.webSocket) {
      this.webSocket.close(1000, 'Client disconnect');
      this.webSocket = null;
    }

    this.wsReconnectAttempt = 0;
    this.setConnected(false);
  }

  isSSEConnected(): boolean {
    if (this._useWebSocket) {
      return this.webSocket?.readyState === WebSocket.OPEN;
    }
    return this.eventSource?.readyState === EventSource.OPEN;
  }

  // ── SSE (Legacy — Express dev server) ─────────────────────

  private connectEventSource(): void {
    if (this.eventSource) return;

    this.eventSource = new EventSource(`${this.baseUrl}/events`);

    this.eventSource.onmessage = (event) => {
      try {
        const parsed: SSEEvent = JSON.parse(event.data);
        this.emit(parsed.type, parsed);
      } catch (e) {
        console.warn('Failed to parse SSE event:', e);
      }
    };

    this.eventSource.onerror = () => {
      console.warn('SSE connection lost, reconnecting in 3s...');
      this.setConnected(false);
      this.eventSource?.close();
      this.eventSource = null;
      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connectEventSource(), 3000);
    };

    this.eventSource.onopen = () => {
      console.log('SSE connected');
      this.setConnected(true);
    };
  }

  // ── WebSocket (Cloudflare Durable Object) ─────────────────

  private connectWebSocket(): void {
    if (this.webSocket) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    const userId = authManager.userId;
    const wsUrl = `${protocol}//${host}/api/ws?userId=${encodeURIComponent(userId)}`;

    this.webSocket = new WebSocket(wsUrl);

    this.webSocket.onopen = () => {
      console.log('WebSocket connected');
      this.wsReconnectAttempt = 0; // Reset backoff on successful connection
      this.setConnected(true);
      this.startPing();
      this.setupVisibilityHandler();

      // Emit connection:restored for downstream recovery
      this.emit('connection:restored', {
        type: 'connection:restored',
        data: {},
        timestamp: Date.now(),
      });
    };

    this.webSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') return;

        this.emit(data.type, {
          type: data.type,
          data: data,
          timestamp: data.timestamp || Date.now(),
        });
      } catch (e) {
        console.warn('Failed to parse WebSocket message:', e);
      }
    };

    this.webSocket.onerror = () => {
      console.warn('WebSocket error');
    };

    this.webSocket.onclose = () => {
      this.setConnected(false);
      this.webSocket = null;

      if (this.wsReconnectAttempt >= this.WS_MAX_RECONNECT_ATTEMPTS) {
        console.error(`WebSocket: gave up after ${this.WS_MAX_RECONNECT_ATTEMPTS} attempts`);
        this.emit('connection:failed', {
          type: 'connection:failed',
          data: { attempts: this.wsReconnectAttempt },
          timestamp: Date.now(),
        });
        return;
      }

      const delay = Math.min(
        this.WS_BACKOFF_BASE_MS * Math.pow(2, this.wsReconnectAttempt),
        this.WS_BACKOFF_CAP_MS,
      ) * (0.5 + Math.random() * 0.5); // Add jitter

      this.wsReconnectAttempt++;
      console.warn(`WebSocket closed, reconnecting in ${Math.round(delay)}ms (attempt ${this.wsReconnectAttempt}/${this.WS_MAX_RECONNECT_ATTEMPTS})...`);

      if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
      this.reconnectTimer = setTimeout(() => this.connectWebSocket(), delay);
    };
  }

  /**
   * Set up visibility change handler — attempt immediate reconnect when tab becomes visible.
   */
  private setupVisibilityHandler(): void {
    if (this.visibilityHandler) return; // Already set up

    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && !this._connected && this._useWebSocket) {
        console.log('Tab visible — attempting immediate WebSocket reconnect');
        this.wsReconnectAttempt = 0; // Reset backoff on manual reconnect
        if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
        this.connectWebSocket();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private startPing(): void {
    if (this.pingInterval) clearInterval(this.pingInterval);
    this.pingInterval = setInterval(() => {
      if (this.webSocket?.readyState === WebSocket.OPEN) {
        this.webSocket.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000);
  }

  // ── Event Subscription ────────────────────────────────────

  onSSE(type: string, listener: SSEListener): () => void {
    if (!this.sseListeners.has(type)) {
      this.sseListeners.set(type, new Set());
    }
    this.sseListeners.get(type)!.add(listener);

    return () => {
      this.sseListeners.get(type)?.delete(listener);
    };
  }

  private emit(type: string, event: SSEEvent): void {
    this.sseListeners.get(type)?.forEach(fn => fn(event));
    this.sseListeners.get('*')?.forEach(fn => fn(event));
  }
}

// Singleton export
export const apiClient = new APIClient();
